import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { 
  Crown, 
  Check, 
  QrCode, 
  Sparkles,
  Tag,
  ArrowLeft,
  Loader2,
  Gift
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CardPremium = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [userCards, setUserCards] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  // Pricing
  const prices = {
    monthly: { price: 99, duration: '30 days' },
    yearly: { price: 999, duration: '1 year', savings: '16%' }
  };

  // Get card username from URL params
  const cardFromUrl = searchParams.get('card');

  // Fetch user's cards that need premium
  useEffect(() => {
    const fetchCards = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const token = await user.getIdToken();
        const response = await fetch(`${API_URL}/api/cards/my-cards`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const cards = await response.json();
          // Filter cards that need premium (expired or no premium)
          const now = new Date();
          const cardsNeedingPremium = cards.filter(card => {
            const isPremiumCard = card.is_premium_card === true;
            const premiumExpiresAt = card.premium_expires_at ? new Date(card.premium_expires_at) : null;
            const hasActivePremium = isPremiumCard && premiumExpiresAt && premiumExpiresAt > now;
            return !hasActivePremium;
          });
          setUserCards(cardsNeedingPremium);

          // Auto-select card from URL
          if (cardFromUrl) {
            const cardToSelect = cardsNeedingPremium.find(c => c.username === cardFromUrl);
            if (cardToSelect) {
              setSelectedCard(cardToSelect);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching cards:', error);
      }
    };

    const fetchCoupons = async () => {
      try {
        // Fetch only coupons applicable to card_premium
        const response = await fetch(`${API_URL}/api/coupons/available?plan_type=card_premium`);
        if (response.ok) {
          const coupons = await response.json();
          setAvailableCoupons(coupons);
        }
      } catch (error) {
        console.error('Error fetching coupons:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCards();
    fetchCoupons();
  }, [user, cardFromUrl]);

  // Apply coupon
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast({
        title: "Enter coupon code",
        description: "Please enter a coupon code to apply",
        variant: "destructive"
      });
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/coupons/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: couponCode.toUpperCase() })
      });

      if (response.ok) {
        const coupon = await response.json();
        setAppliedCoupon(coupon);
        toast({
          title: "Coupon Applied!",
          description: `${coupon.discount}% discount applied`
        });
      } else {
        const error = await response.json();
        toast({
          title: "Invalid Coupon",
          description: error.detail || "This coupon is not valid",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to validate coupon",
        variant: "destructive"
      });
    }
  };

  // Calculate final price
  const calculatePrice = () => {
    const basePrice = prices[billingPeriod].price;
    if (appliedCoupon) {
      const discount = (basePrice * appliedCoupon.discount) / 100;
      return Math.round(basePrice - discount);
    }
    return basePrice;
  };

  // Handle purchase
  const handlePurchase = async () => {
    if (!user) {
      toast({
        title: "Sign In Required",
        description: "Please sign in to purchase Card Premium",
        variant: "destructive"
      });
      window.dispatchEvent(new CustomEvent('openSignInModal'));
      return;
    }

    if (!selectedCard) {
      toast({
        title: "Select a Card",
        description: "Please select which card you want to upgrade",
        variant: "destructive"
      });
      return;
    }

    setProcessing(true);
    try {
      const token = await user.getIdToken();
      
      // Create payment for card premium
      const paymentData = {
        plan_type: 'card_premium',
        plan_id: `card_premium_${billingPeriod}`,
        card_username: selectedCard.username,
        amount: prices[billingPeriod].price,
        final_amount: calculatePrice(),
        coupon_code: appliedCoupon?.code || null,
        billing_period: billingPeriod
      };

      // Dispatch event to open payment modal
      const event = new CustomEvent('showPaymentModal', {
        detail: {
          planType: 'card_premium',
          planId: `card_premium_${billingPeriod}`,
          price: calculatePrice(),
          memberCount: 1,
          totalAmount: calculatePrice(),
          name: `Card Premium (${selectedCard.username}) - ${billingPeriod === 'monthly' ? 'Monthly' : 'Annual'}`,
          cardUsername: selectedCard.username,
          couponCode: appliedCoupon?.code || null
        }
      });
      window.dispatchEvent(event);
      
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to process payment",
        variant: "destructive"
      });
    } finally {
      setProcessing(false);
    }
  };

  // Select coupon from available list
  const handleSelectCoupon = (coupon) => {
    setCouponCode(coupon.code);
    setAppliedCoupon(coupon);
    toast({
      title: "Coupon Applied!",
      description: `${coupon.discount}% discount applied`
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-purple-50 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>

          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-amber-500 to-purple-600 rounded-full mb-4">
              <Crown className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Card Premium
            </h1>
            <p className="text-gray-600">
              Unlock premium features for your digital business card
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - Card Selection & Pricing */}
            <div className="space-y-6">
              {/* Card Selection */}
              <Card className="border-2 border-gray-200">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <QrCode className="w-5 h-5 text-amber-600" />
                    Select Card to Upgrade
                  </h3>
                  
                  {userCards.length === 0 ? (
                    <div className="text-center py-6 text-gray-500">
                      <p>All your cards already have active premium!</p>
                      <Button
                        variant="outline"
                        onClick={() => navigate('/profile')}
                        className="mt-4"
                      >
                        Go to Profile
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {userCards.map((card) => (
                        <div
                          key={card.id || card.username}
                          onClick={() => setSelectedCard(card)}
                          className={`p-3 rounded-lg border cursor-pointer transition-all ${
                            selectedCard?.username === card.username
                              ? 'bg-amber-50 border-amber-400 ring-2 ring-amber-200'
                              : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              card.nfc_code ? 'bg-amber-100' : 'bg-gray-100'
                            }`}>
                              {card.nfc_code ? (
                                <Sparkles className="w-5 h-5 text-amber-600" />
                              ) : (
                                <QrCode className="w-5 h-5 text-gray-600" />
                              )}
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-gray-900">@{card.username}</p>
                              <p className="text-xs text-gray-500">{card.name}</p>
                            </div>
                            {selectedCard?.username === card.username && (
                              <Check className="w-5 h-5 text-amber-600" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Billing Period */}
              <Card className="border-2 border-gray-200">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-gray-900 mb-4">Select Plan</h3>
                  
                  <div className="space-y-3">
                    {/* Monthly */}
                    <div
                      onClick={() => setBillingPeriod('monthly')}
                      className={`p-4 rounded-lg border cursor-pointer transition-all ${
                        billingPeriod === 'monthly'
                          ? 'bg-amber-50 border-amber-400'
                          : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-gray-900">Monthly</p>
                          <p className="text-xs text-gray-500">30 days premium</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-amber-600">₹99</p>
                          <p className="text-xs text-gray-500">/month</p>
                        </div>
                      </div>
                    </div>

                    {/* Yearly */}
                    <div
                      onClick={() => setBillingPeriod('yearly')}
                      className={`p-4 rounded-lg border cursor-pointer transition-all ${
                        billingPeriod === 'yearly'
                          ? 'bg-amber-50 border-amber-400'
                          : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-gray-900">
                            Yearly
                            <Badge className="ml-2 bg-green-600">Save 16%</Badge>
                          </p>
                          <p className="text-xs text-gray-500">1 year premium</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-amber-600">₹999</p>
                          <p className="text-xs text-gray-500">/year</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Features & Checkout */}
            <div className="space-y-6">
              {/* Features */}
              <Card className="border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-white">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Crown className="w-5 h-5 text-amber-600" />
                    Card Premium Features
                  </h3>
                  
                  <ul className="space-y-3">
                    {[
                      'Upload Company Logo',
                      'Logo Embedded in QR Code',
                      'No Piper Card Watermark',
                      'Premium Card Templates',
                      'Priority Support',
                      'Analytics Dashboard'
                    ].map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-gray-700">
                        <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* Available Coupons */}
              {availableCoupons.length > 0 && (
                <Card className="border-2 border-green-200 bg-green-50">
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Gift className="w-5 h-5 text-green-600" />
                      Available Offers
                    </h3>
                    
                    <div className="space-y-2">
                      {availableCoupons.map((coupon) => (
                        <div
                          key={coupon.code}
                          onClick={() => handleSelectCoupon(coupon)}
                          className={`p-3 rounded-lg border cursor-pointer transition-all ${
                            appliedCoupon?.code === coupon.code
                              ? 'bg-green-100 border-green-400'
                              : 'bg-white border-green-200 hover:bg-green-50'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-mono font-bold text-green-700">{coupon.code}</p>
                              <p className="text-xs text-gray-600">{coupon.description || `${coupon.discount}% off`}</p>
                            </div>
                            <Badge className="bg-green-600">{coupon.discount}% OFF</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Coupon Code Input */}
              <Card className="border-2 border-gray-200">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Tag className="w-5 h-5 text-amber-600" />
                    Have a Coupon?
                  </h3>
                  
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter coupon code"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      className="flex-1"
                    />
                    <Button
                      variant="outline"
                      onClick={handleApplyCoupon}
                      disabled={!!appliedCoupon}
                    >
                      Apply
                    </Button>
                  </div>
                  
                  {appliedCoupon && (
                    <div className="mt-3 p-2 bg-green-50 rounded-lg border border-green-200">
                      <p className="text-sm text-green-700 flex items-center gap-1">
                        <Check className="w-4 h-4" />
                        Coupon applied: {appliedCoupon.discount}% off
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Checkout Summary */}
              <Card className="border-2 border-amber-400 bg-gradient-to-r from-amber-50 to-purple-50">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-gray-900 mb-4">Order Summary</h3>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Card Premium ({billingPeriod})</span>
                      <span className="text-gray-900">₹{prices[billingPeriod].price}</span>
                    </div>
                    
                    {appliedCoupon && (
                      <div className="flex justify-between text-sm text-green-600">
                        <span>Discount ({appliedCoupon.discount}%)</span>
                        <span>-₹{Math.round((prices[billingPeriod].price * appliedCoupon.discount) / 100)}</span>
                      </div>
                    )}
                    
                    <div className="border-t pt-2 flex justify-between font-bold">
                      <span>Total</span>
                      <span className="text-xl text-amber-600">₹{calculatePrice()}</span>
                    </div>
                  </div>

                  {selectedCard && (
                    <div className="mb-4 p-2 bg-white rounded-lg border border-gray-200">
                      <p className="text-xs text-gray-500">Upgrading:</p>
                      <p className="font-medium text-gray-900">@{selectedCard.username}</p>
                    </div>
                  )}

                  <Button
                    onClick={handlePurchase}
                    disabled={!selectedCard || processing}
                    className="w-full bg-gradient-to-r from-amber-500 to-purple-600 hover:from-amber-600 hover:to-purple-700 text-white py-6"
                  >
                    {processing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Crown className="w-4 h-4 mr-2" />
                        {selectedCard ? 'Pay Now' : 'Select a Card First'}
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CardPremium;
